import React from 'react';
import { observer } from 'mobx-react';
import { useTheme, Box, TextField, IconButton, Tooltip } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DesktopDateTimePicker, DesktopDatePicker } from '@mui/x-date-pickers';

import ClearIcon from '@mui/icons-material/Clear';
import { DateTimeQcFieldName } from './const';
import useData from './hooks';
import { ValueFieldsTypesProps } from '50.quickConnect.Fields/FieldsTypes/types';
import { DateTimeFieldType } from '90.quickConnect.Models/enums';
import QcTooltip from '50.quickConnect.Fields/QcTooltip';
import { FieldMainBox, FlexRow, FlexMainBoxRow } from '50.quickConnect.Fields/styles';
import { DateTimeDesc } from '90.quickConnect.Models/models';
import { useStore } from '30.quickConnect.Stores';

const DateTimeQcField = ({
  field,
  updateDeclaration,
  labelSpan,
  setSelectedIndex,
  isReadOnly,
  flattenFields,
  labelClass,
}: ValueFieldsTypesProps) => {
  const dateTimeDesc = field as DateTimeDesc;
  const { toolTip, label, id, type, fullPathId, isVertical } = dateTimeDesc;
  const nameWithId = `${DateTimeQcFieldName}-${id}`;

  const {
    t,
    localValue,
    locale,
    onChange,
    minDateAllowed,
    maxDateAllowed,
    open,
    placeholderDateLocale,
    placeholderDateTimeLocale,
    handleOnChange,
    handleSelectField,
    handleOpenPicker,
    handlePickerClose,
    handleClear,
  } = useData(dateTimeDesc, updateDeclaration, flattenFields, setSelectedIndex, isReadOnly);

  const {
    DeclarationStore: { verifRequiredField, requiredField },
  } = useStore();

  const { breakpoints } = useTheme();

  const isTestingMode =
    typeof navigator !== 'undefined' &&
    (navigator.webdriver ||
      typeof (window as Window & typeof globalThis & { Cypress?: unknown }).Cypress !== 'undefined');

  const labelBox = label ? (
    <Box sx={isVertical === false ? { ...FlexRow, flex: 2, width: 'auto' } : FlexRow}>
      <Box component="span" sx={{ flex: 1 }}>
        {labelSpan}
      </Box>

      {toolTip && <QcTooltip parentId={id} tooltip={toolTip} />}
    </Box>
  ) : (
    <></>
  );

  return (
    <Box
      className={labelClass}
      id={`scrollToAnchor-${fullPathId}`}
      sx={isVertical === false ? { ...FlexMainBoxRow(breakpoints), gap: 2 } : FieldMainBox(breakpoints)}
      data-cy={nameWithId}
      onClick={handleSelectField}
    >
      {labelBox}

      <Box sx={isVertical === false ? { flex: 1 } : {}}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
          {(type === DateTimeFieldType.Date || !type) && (
            <DesktopDatePicker
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  onChange={handleOnChange}
                  data-cy={`${nameWithId}-date`}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      placeholder: placeholderDateLocale,
                      readOnly: !isTestingMode,
                      // En mode test, l'input est saisi directement : le picker ouvert masquerait les champs suivants
                      onClick: isTestingMode ? undefined : handleOpenPicker,
                    },
                  }}
                />
              )}
              value={localValue}
              open={open}
              onOpen={handleOpenPicker}
              onClose={handlePickerClose}
              onChange={onChange}
              InputProps={{
                startAdornment: (
                  <Tooltip title={t('formlib_array_deleted_title').toString()} style={{ order: 1 }}>
                    <span>
                      <IconButton
                        data-cy={`${nameWithId}-clear`}
                        edge="end"
                        onClick={handleClear}
                        disabled={isReadOnly}
                      >
                        <ClearIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                ),
              }}
              InputAdornmentProps={{
                position: 'end',
                style: { order: 2, marginLeft: 0 },
              }}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              minDate={minDateAllowed}
              maxDate={maxDateAllowed}
            />
          )}

          {type === DateTimeFieldType.DateTime && (
            <DesktopDateTimePicker
              inputRef={(input) => {
                if (input != null && verifRequiredField && requiredField?.fullPathId === fullPathId) {
                  input.focus();
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  data-cy={`${nameWithId}-dateTime`}
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      placeholder: placeholderDateTimeLocale,
                      readOnly: !isTestingMode,
                      // En mode test, l'input est saisi directement : le picker ouvert masquerait les champs suivants
                      onClick: isTestingMode ? undefined : handleOpenPicker,
                    },
                  }}
                />
              )}
              value={localValue}
              open={open}
              onOpen={handleOpenPicker}
              onClose={handlePickerClose}
              onChange={onChange}
              InputProps={{
                startAdornment: (
                  <Tooltip title={t('formlib_array_deleted_title').toString()} style={{ order: 1 }}>
                    <span>
                      <IconButton
                        data-cy={`${nameWithId}-clear`}
                        edge="end"
                        onClick={handleClear}
                        disabled={isReadOnly}
                      >
                        <ClearIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                ),
              }}
              InputAdornmentProps={{
                position: 'end',
                style: { order: 2, marginLeft: 0 },
              }}
              readOnly={isReadOnly}
              disabled={isReadOnly}
              minDateTime={minDateAllowed}
              maxDateTime={maxDateAllowed}
            />
          )}
        </LocalizationProvider>
      </Box>
    </Box>
  );
};

export default observer(DateTimeQcField);
